import { FC, memo, MouseEvent, useEffect, useRef, useState } from 'react'

import { CircularProgress, IconButton, Skeleton, Stack, Typography, useTheme } from '@mui/material'
import Grid from '@mui/system/Unstable_Grid'
import { MoreVert } from '@mui/icons-material'

import PerfectScrollbar from 'react-perfect-scrollbar'
import { useTranslation } from 'react-i18next'
import { Draggable, Droppable } from 'react-beautiful-dnd'

import { useMentorCloud } from 'src/contexts/MentorCloudContext'

import { useMentorCloudInfiniteFiles } from 'src/hooks/files/useMentorCloudInfiniteFiles'
import { useMentorCloudFolders } from 'src/hooks/files/useMentorCloudFolders'
import { usePopover } from 'src/hooks/usePopover'

import { MentorCloudFileItem } from '../MentorCloudFileItem/MentorCloudFileItem'
import { MentorCloudFilePopover } from '../MentorCloudFilePopover/MentorCloudFilePopover'
import { MentorCloudFolderPopover } from '../MentorCloudFolderPopover/MentorCloudFolderPopover'

import { Folder, MentorCloudFile, MentorCloudFileFilters } from 'src/types/files/file'

const skeletons = new Array(30).fill(0)

interface MentorCloudInfiniteListProps {
  isModal?: boolean
  view: 'grid' | 'list'
  activeFolder?: Folder | null
  activeFile?: MentorCloudFile
  movedFileId?: number | null
  onClickFile?: (file: MentorCloudFile) => void
  onClickFolder?: (folder: Folder) => void
  filters: MentorCloudFileFilters
}

export const MentorCloudInfiniteList: FC<MentorCloudInfiniteListProps> = memo(
  ({ isModal, filters, activeFolder, activeFile, view, movedFileId, onClickFile, onClickFolder }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'PAGE.MENTORCLOUD' })

    const { uploadFiles } = useMentorCloud()

    const theme = useTheme()

    const [popoverFile, setPopoverFile] = useState<MentorCloudFile | null>(null)
    const [popoverFolder, setPopoverFolder] = useState<Folder | null>(null)
    const { anchor, id, isOpen, close, open } = usePopover('file')
    const {
      anchor: folderPopoverAnchor,
      id: folderPopoverId,
      isOpen: isFolderPopoverOpen,
      close: closeFolderPopover,
      open: openFolderPopover
    } = usePopover('folder')

    const { data: folders = [] } = useMentorCloudFolders(activeFolder?.id)
    const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } = useMentorCloudInfiniteFiles(
      filters,
      activeFolder?.id || null
    )

    const observerRef = useRef<IntersectionObserver | null>(null)
    const lastElementRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
      if (!lastElementRef.current || !hasNextPage) return

      observerRef.current = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !isFetchingNextPage) {
          fetchNextPage()
        }
      })

      observerRef.current.observe(lastElementRef.current)

      return () => observerRef.current?.disconnect()
    }, [hasNextPage, fetchNextPage, isFetchingNextPage])

    const openFilePopover = (file: MentorCloudFile) => (e: MouseEvent) => {
      open(e)
      setPopoverFile(file)
    }

    const openPopover = (folder: Folder) => (e: MouseEvent) => {
      openFolderPopover(e)
      setPopoverFolder(folder)
    }

    return (
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: `calc(100vh - ${theme.spacing(isModal ? 74 : 64)})`,
          overflowX: 'hidden'
        }}
      >
        <PerfectScrollbar options={{ suppressScrollX: true }}>
          <Grid
            width={'100%'}
            container
            rowSpacing={theme.spacing(4)}
            columnSpacing={theme.spacing(4)}
            columns={view === 'grid' ? 10 : 1}
            sx={{ overflowX: 'hidden' }}
          >
            {folders?.map(folder => (
              <Droppable droppableId={`folder-${folder.id.toString()}`} key={folder.id}>
                {provided => (
                  <>
                    {view === 'grid' ? (
                      <Grid
                        xs={5}
                        md={3.3}
                        lg={2.5}
                        xl={2}
                        key={folder.id}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        <MentorCloudFileItem
                          view={view}
                          onClickFolder={onClickFolder}
                          folder={folder}
                          renderAfter={() => (
                            <IconButton
                              onClick={openPopover(folder)}
                              sx={{ padding: theme.spacing(0, 0.5) }}
                              color='primary'
                            >
                              <MoreVert />
                            </IconButton>
                          )}
                        />
                      </Grid>
                    ) : (
                      <Grid xs={1} ref={provided.innerRef} {...provided.droppableProps}>
                        <MentorCloudFileItem
                          view={view}
                          onClickFolder={onClickFolder}
                          folder={folder}
                          renderAfter={() => (
                            <IconButton
                              onClick={openPopover(folder)}
                              sx={{ padding: theme.spacing(0, 0.5) }}
                              color='primary'
                            >
                              <MoreVert />
                            </IconButton>
                          )}
                        />
                      </Grid>
                    )}
                    {provided.placeholder && <div style={{ display: 'none' }}>{provided.placeholder}</div>}
                  </>
                )}
              </Droppable>
            ))}
            {uploadFiles?.map((file, i) => {
              if (file.progress === 100 && file.convertProgress !== undefined && file.convertProgress === 0) return

              return (
                <>
                  {activeFolder?.id === file.parentFolderId && (
                    <>
                      {file?.convertProgress !== undefined ? (
                        <Grid xs={5} md={3.3} lg={2.5} xl={2} key={i}>
                          <Stack
                            width={'100%'}
                            height={theme.spacing(view === 'grid' ? 38.5 : 10.5)}
                            borderRadius={theme.spacing(1.5)}
                            bgcolor={theme.palette.secondary[300]}
                            justifyContent={'center'}
                            alignItems={'center'}
                            gap={theme.spacing(2)}
                          >
                            <CircularProgress
                              variant={file.progress !== 100 ? 'determinate' : 'indeterminate'}
                              value={file.progress !== 100 ? file.progress : file.convertProgress}
                            />
                            <Typography
                              variant='caption'
                              color={theme.palette.primary[500]}
                              fontWeight={theme.typography.fontWeightBold}
                            >
                              {file.progress !== 100 ? t('progress.upload') : t('progress.convert')}
                            </Typography>
                            {file.progress !== 100 && (
                              <Typography
                                variant='caption'
                                color={theme.palette.primary[500]}
                                fontWeight={theme.typography.fontWeightBold}
                              >
                                {file.progress.toFixed(0)}%
                              </Typography>
                            )}
                          </Stack>
                        </Grid>
                      ) : (
                        <Grid xs={5} md={3.3} lg={2.5} xl={2} key={i}>
                          <Stack
                            width={'100%'}
                            height={theme.spacing(view === 'grid' ? 38.5 : 10.5)}
                            borderRadius={theme.spacing(1.5)}
                            bgcolor={theme.palette.secondary[300]}
                            justifyContent={'center'}
                            alignItems={'center'}
                            gap={theme.spacing(2)}
                          >
                            <CircularProgress variant={'determinate'} value={file.progress} />
                          </Stack>
                        </Grid>
                      )}
                    </>
                  )}
                </>
              )
            })}
            {data?.pages.map((page, pageIndex) =>
              page
                .filter(file => file.id !== movedFileId)
                .map((file, index) => (
                  <Draggable draggableId={file.id.toString()} index={index + (pageIndex + 1) * 30} key={file.id}>
                    {provided => (
                      <Grid
                        xs={view === 'grid' ? 5 : 1}
                        md={view === 'grid' ? 3.3 : undefined}
                        lg={view === 'grid' ? 2.5 : undefined}
                        xl={view === 'grid' ? 2 : undefined}
                        key={file.id}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <MentorCloudFileItem
                          file={file}
                          view={view}
                          isActive={activeFile?.id === file?.id}
                          onClickFile={onClickFile}
                          renderAfter={() => (
                            <IconButton
                              onClick={openFilePopover(file)}
                              sx={{ padding: theme.spacing(0, 1) }}
                              color='primary'
                            >
                              <MoreVert />
                            </IconButton>
                          )}
                        />
                      </Grid>
                    )}
                  </Draggable>
                ))
            )}
            {(isFetchingNextPage || isLoading) &&
              skeletons.map((_, i) => (
                <Grid key={i} xs={5} md={3.3} lg={2.5} xl={2}>
                  <Skeleton
                    width={'100%'}
                    height={theme.spacing(view === 'grid' ? 38.5 : 10.5)}
                    style={{ transform: 'none' }}
                  />
                </Grid>
              ))}
            {!isLoading && <div ref={lastElementRef}></div>}
          </Grid>
        </PerfectScrollbar>
        <MentorCloudFilePopover
          filters={filters}
          id={id}
          anchor={anchor}
          parentFolderId={activeFolder?.id}
          onChangePopoverFile={setPopoverFile}
          file={popoverFile}
          close={close}
          isOpen={isOpen}
        />
        <MentorCloudFolderPopover
          filters={filters}
          id={folderPopoverId}
          anchor={folderPopoverAnchor}
          parentFolderId={activeFolder?.id}
          onChangePopoverFolder={setPopoverFolder}
          folder={popoverFolder}
          close={closeFolderPopover}
          isOpen={isFolderPopoverOpen}
        />
      </Stack>
    )
  }
)
